import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  makeStyles,
} from '@material-ui/core';
import { Content, Page } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import RegisterForm from './RegisterForm';
import ThreadTable from './ThreadTable';
import ThreadDetail from './ThreadDetail';
import DeleteThreadDialog from './DeleteThreadDialog';
import SplitPanel from './SplitPanel';
import MergePanel from './MergePanel';
import { useLifecycleStore, PAGE_SIZE_DEFAULT } from './useLifecycleStore';
import type { LineageRef } from './ThreadDetail';
import {
  mergeReviewTargetId,
  selectedMergeable,
  type ImportMode,
  type RequirementInstance,
  type RequirementThread,
  type ThreadStatus,
} from './types';
import type { ApprovalWarning, SplitChildInput } from './api';
import ApprovalWarningsDialog from './ApprovalWarningsDialog';
import {
  ActiveProjectBar,
  RequireActiveProject,
  useActiveProject,
} from '../activeProject';
import { canPerform, useProjectRole } from '../../roles';

import { useSetTopBarTitle } from '../shell/TopBarTitleContext';

const useStyles = makeStyles(theme => ({
  contentCard: {
    backgroundColor: theme.palette.background.paper,
    padding: 24,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 900,
  },
}));

const SEARCH_DEBOUNCE_MS = 300;

type View =
  | { kind: 'list' }
  | { kind: 'import' }
  | { kind: 'detail'; threadId: string }
  | {
      kind: 'split';
      source: RequirementThread;
      sourceText: string;
      // Review re-entry of an already-pending Split
      reviewMode?: boolean;
    }
  | {
      kind: 'merge';
      sources: RequirementThread[];
      // Review re-entry of an already-pending Merge
      reviewMode?: boolean;
      targetThreadId?: string;
    };

function currentText(instances: RequirementInstance[]): string {
  // Prefer CURRENT, fall back to APPROVED — CURRENT must win regardless of order.
  const cur =
    instances.find(i => i.status === 'CURRENT') ??
    instances.find(i => i.status === 'APPROVED');
  return cur?.text ?? '';
}

interface RequirementLifecycleViewProps {
  projectId: number;
}

const RequirementLifecycleView: FC<RequirementLifecycleViewProps> = ({
  projectId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');
  const location = useLocation();
  const store = useLifecycleStore(projectId);
  // The backend refuses what the role does not allow; hiding those actions
  // keeps the screen from offering a button that can only end in a 403.
  const { activeProject } = useActiveProject();
  const projectRole = useProjectRole(activeProject);
  const canWrite = canPerform(projectRole, 'work-item.write');
  const canApprove = canPerform(projectRole, 'work-item.approve');

  const [view, setView] = useState<View>({ kind: 'list' });
  const [statusFilter, setStatusFilter] = useState<ThreadStatus | ''>('');
  const [search, setSearch] = useState('');
  const [includeTerminal, setIncludeTerminal] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<RequirementThread | null>(
    null,
  );
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approveForce, setApproveForce] = useState(false);
  // Non-null while the ASPICE gate has blocked a force-approve.
  const [gateWarnings, setGateWarnings] = useState<ApprovalWarning[] | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Pending Split/Merge proposal results (set after Propose succeeds, or loaded
  // for review re-entry of an already-pending operation).
  const [pendingChildren, setPendingChildren] = useState<RequirementThread[]>(
    [],
  );
  // Child threads' current texts for Split review
  const [pendingChildrenTexts, setPendingChildrenTexts] = useState<
    Record<string, string>
  >({});
  const [pendingTarget, setPendingTarget] = useState<RequirementThread | null>(
    null,
  );
  const [pendingSources, setPendingSources] = useState<RequirementThread[]>([]);
  // Integration target's current text, shown/edited in the Merge review.
  const [pendingTargetText, setPendingTargetText] = useState('');
  // Source threads' current texts, shown in the Merge review
  const [pendingSourcesTexts, setPendingSourcesTexts] = useState<
    Record<string, string>
  >({});
  // Resolved lineage relatives for the detail view
  const [lineage, setLineage] = useState<LineageRef[]>([]);

  const { loadThreads, loadDetail } = store;

  // Reset to the list whenever the user navigates here via the sidebar.
  useEffect(() => {
    setView({ kind: 'list' });
  }, [location.key]);

  // Load the list when showing it (and when the filters change).
  // search is debounced to avoid firing on every keystroke.
  useEffect(() => {
    if (view.kind !== 'list') {
      return undefined;
    }
    const timerId = setTimeout(() => {
      void loadThreads({
        status: statusFilter || undefined,
        search: search || undefined,
        includeTerminal,
        page,
        pageSize,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [
    view.kind,
    statusFilter,
    search,
    includeTerminal,
    page,
    pageSize,
    loadThreads,
  ]);

  // Load detail data when entering the detail view.
  useEffect(() => {
    if (view.kind === 'detail') {
      void loadDetail(view.threadId);
    }
  }, [view, loadDetail]);

  // Resolve lineage relatives (names/links) whenever the detail thread changes
  // Parent / merged-into come from the thread's own pointers;
  // split children / merge sources are discovered by scanning the project.
  const { thread: detailThread, resolveThreads, loadLineageRelatives } = store;
  useEffect(() => {
    let cancelled = false;
    if (view.kind !== 'detail' || !detailThread) {
      setLineage([]);
      return () => {
        cancelled = true;
      };
    }
    const refs: LineageRef[] = [];
    void (async () => {
      // Upward pointers: this thread is a split child or a merge source.
      const upIds: { id: string; relation: LineageRef['relation'] }[] = [];
      if (detailThread.parentThreadId) {
        upIds.push({ id: detailThread.parentThreadId, relation: 'parent' });
      }
      if (detailThread.mergedIntoThreadId) {
        upIds.push({
          id: detailThread.mergedIntoThreadId,
          relation: 'mergedInto',
        });
      }
      if (upIds.length > 0) {
        const resolved = await resolveThreads(upIds.map(u => u.id));
        if (cancelled) return;
        upIds.forEach(u => {
          const rt = resolved.find(r => r.threadId === u.id);
          if (rt) {
            refs.push({
              threadId: rt.threadId,
              name: rt.name,
              status: rt.status,
              relation: u.relation,
            });
          }
        });
      }
      // Downward pointers: split children / merge sources that point back at
      // this thread. Status-agnostic (excludes only soft-deleted) so the lineage
      // keeps rendering them after the operation is accepted — children move to
      // WIP and sources to MERGED, which the PENDING-only review loaders miss.
      const { children, sources } = await loadLineageRelatives(
        detailThread.threadId,
      );
      if (cancelled) return;
      children.forEach(c =>
        refs.push({
          threadId: c.threadId,
          name: c.name,
          status: c.status,
          relation: 'splitChild',
        }),
      );
      sources.forEach(srcThread =>
        refs.push({
          threadId: srcThread.threadId,
          name: srcThread.name,
          status: srcThread.status,
          relation: 'mergeSource',
        }),
      );
      if (!cancelled) setLineage(refs);
    })();
    return () => {
      cancelled = true;
    };
  }, [view.kind, detailThread, resolveThreads, loadLineageRelatives]);

  const reloadList = () =>
    loadThreads({
      status: statusFilter || undefined,
      search: search || undefined,
      includeTerminal,
      page,
      pageSize,
    });

  const goList = () => {
    setView({ kind: 'list' });
    setPendingChildren([]);
    setPendingChildrenTexts({});
    setPendingTarget(null);
    setPendingSources([]);
    setPendingTargetText('');
    setPendingSourcesTexts({});
    // No explicit reload here — the useEffect fires loadThreads when view.kind changes to 'list'.
  };

  const handleRegister = async (
    texts: string[],
    mode: ImportMode,
  ): Promise<number | null> => {
    const results = await store.register(texts, mode);
    if (results === null) {
      return null;
    }
    setToast(
      t('register.success', {
        count: results.length,
        status: mode === 'approved' ? 'APPROVED' : 'DRAFT',
      }),
    );
    // RegisterForm calls onBack (= goList) on success, which reloads the list;
    // no inline reload needed here.
    return results.length;
  };

  // Wrap a mutation with the busy flag so buttons disable during calls.
  const withBusy = async (fn: () => Promise<boolean>): Promise<boolean> => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
    }
  };

  // Submit (DRAFT → WIP). Used from both the list rows and the detail view.
  // `after` lets the caller refresh the right surface (list vs detail).
  const handleSubmit = (threadId: string, after: () => void) =>
    withBusy(async () => {
      const ok = await store.submitThread(threadId);
      if (ok) {
        setToast(t('s01.submitSuccess'));
        after();
      }
      return ok;
    });

  // Open the Split screen for a list-row thread: resolve the source from the
  // loaded list and fetch its current text (display-only) without entering the
  // detail view. Avoids stale-closure reads of store.thread/instances.
  const openSplitFromList = async (threadId: string) => {
    const source = store.threads.find(x => x.threadId === threadId);
    if (!source) return;
    setPendingChildren([]);
    setPendingChildrenTexts({});
    const instances = await store.fetchInstances(threadId);
    setView({ kind: 'split', source, sourceText: currentText(instances) });
  };

  // -- Pending review re-entry --------------------------------
  // Open the Split/Merge review for a thread already parked on a pending
  // operation, reconstructing the proposal from persisted state.

  const openSplitReview = (parentThreadId: string) =>
    withBusy(async () => {
      const review = await store.loadSplitReview(parentThreadId);
      if (!review || !review.parent) return false;
      // Fetch parent and each child's current text in parallel
      const [instances, ...childInstancesList] = await Promise.all([
        store.fetchInstances(parentThreadId),
        ...review.children.map(c => store.fetchInstances(c.threadId)),
      ]);
      const childrenTexts: Record<string, string> = {};
      review.children.forEach((c, i) => {
        childrenTexts[c.threadId] = currentText(childInstancesList[i]);
      });
      setPendingChildren(review.children);
      setPendingChildrenTexts(childrenTexts);
      setView({
        kind: 'split',
        source: review.parent,
        sourceText: currentText(instances),
        reviewMode: true,
      });
      return true;
    });

  const openMergeReview = (targetThreadId: string) =>
    withBusy(async () => {
      const review = await store.loadMergeReview(targetThreadId);
      if (!review) return false;
      // Fetch target text and each source thread's current text in parallel
      const [instances, ...sourceInstancesList] = await Promise.all([
        store.fetchInstances(targetThreadId),
        ...review.sources.map(s => store.fetchInstances(s.threadId)),
      ]);
      const sourcesTexts: Record<string, string> = {};
      review.sources.forEach((s, i) => {
        sourcesTexts[s.threadId] = currentText(sourceInstancesList[i]);
      });
      setPendingTarget(review.target);
      setPendingSources(review.sources);
      setPendingTargetText(currentText(instances));
      setPendingSourcesTexts(sourcesTexts);
      setView({
        kind: 'merge',
        sources: review.sources,
        reviewMode: true,
        targetThreadId,
      });
      return true;
    });

  // Edit the integration target's text while a Merge is pending, then refresh
  // the shown target text
  const handleEditMergeTarget = (text: string) =>
    withBusy(async () => {
      if (!pendingTarget) return false;
      const newInstance = await store.editText(pendingTarget.threadId, text);
      if (newInstance) {
        setPendingTargetText(newInstance.text);
        setToast(t('merge.editTargetSuccess'));
      }
      return newInstance !== null;
    });

  const detailThreadId = view.kind === 'detail' ? view.threadId : '';

  // Selected threads still eligible for Merge. A thread that has since been
  // locked into a Merge (or left WIP) drops out even if its id is still in the
  // set, so "Merge" cannot count or send it.
  const selectedThreads = useMemo(
    () => selectedMergeable(store.threads, selectedIds),
    [store.threads, selectedIds],
  );

  // -- Split handlers -------------------------------------------------------

  const handleProposeSplit = (children: SplitChildInput[]) =>
    withBusy(async () => {
      if (view.kind !== 'split') return false;
      const result = await store.proposeSplit(view.source.threadId, children);
      if (!result) return false;
      setPendingChildren(result.children);
      setToast(t('split.successPropose', { count: result.children.length }));
      return true;
    });

  const handleAcceptSplit = () =>
    withBusy(async () => {
      if (view.kind !== 'split') return false;
      const ok = await store.acceptSplit(view.source.threadId);
      if (ok) {
        setToast(t('split.successAccept'));
        goList();
      }
      return ok;
    });

  const handleRejectSplit = () =>
    withBusy(async () => {
      if (view.kind !== 'split') return false;
      const ok = await store.rejectSplit(view.source.threadId);
      if (ok) {
        setToast(t('split.successReject'));
        goList();
      }
      return ok;
    });

  // -- Merge handlers -------------------------------------------------------

  const handleProposeMerge = (name: string, text: string) =>
    withBusy(async () => {
      if (view.kind !== 'merge') return false;
      const result = await store.proposeMerge({
        sourceThreadIds: view.sources.map(s => s.threadId),
        name,
        text,
      });
      if (!result) return false;
      setPendingTarget(result.target);
      setPendingSources(result.sources);
      // The sources are now locked, so they are no longer a selection to merge.
      setSelectedIds(new Set());
      setToast(t('merge.successPropose'));
      return true;
    });

  const handleAcceptMerge = () =>
    withBusy(async () => {
      if (!pendingTarget) return false;
      const ok = await store.acceptMerge(pendingTarget.threadId);
      if (ok) {
        setToast(t('merge.successAccept'));
        setSelectedIds(new Set());
        goList();
      }
      return ok;
    });

  const handleRejectMerge = () =>
    withBusy(async () => {
      if (!pendingTarget) return false;
      const ok = await store.rejectMerge(pendingTarget.threadId);
      if (ok) {
        setToast(t('merge.successReject'));
        setSelectedIds(new Set());
        goList();
      }
      return ok;
    });

  return (
    <>
      {/* Inside the view, not above it: the picker has to be locked while a
          mutation is in flight, and `busy` lives here. Switching project
          unmounts this view and the in-flight response would be dropped with
          no feedback at all. */}
      <ActiveProjectBar disabled={busy} />

      {store.error && (
        <Alert
          severity="error"
          style={{ marginBottom: 16 }}
          onClose={store.clearError}
        >
          {store.error}
        </Alert>
      )}

      {view.kind === 'list' && (
        <Box className={classes.contentCard}>
          {canWrite && (
            <Box style={{ marginBottom: 16 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setView({ kind: 'import' })}
              >
                {t('s01.importButton')}
              </Button>
            </Box>
          )}
          <ThreadTable
            threads={store.threads}
            loading={store.loading}
            statusFilter={statusFilter}
            onStatusFilterChange={(status: ThreadStatus | '') => {
              // Changing the filter may hide selected rows; clear the
              // selection so Merge can never target invisible/stale threads.
              setSelectedIds(new Set());
              setPage(0);
              setStatusFilter(status);
              // Terminal statuses (DELETED, SPLIT, MERGED, PENDING_*)
              // are excluded by default; auto-enable includeTerminal so they
              // are actually visible when the user explicitly filters for them.
              const terminalStatuses: Array<ThreadStatus | ''> = [
                'DELETED',
                'SPLIT',
                'MERGED',
                'PENDING_SPLIT',
                'PENDING_MERGE',
              ];
              if (terminalStatuses.includes(status)) {
                setIncludeTerminal(true);
              } else {
                setIncludeTerminal(false);
              }
            }}
            search={search}
            onSearchChange={(value: string) => {
              setSelectedIds(new Set());
              setPage(0);
              setSearch(value);
            }}
            includeTerminal={includeTerminal}
            onIncludeTerminalChange={(value: boolean) => {
              setSelectedIds(new Set());
              setPage(0);
              setIncludeTerminal(value);
            }}
            selectedIds={selectedIds}
            // Drive the Merge button from threads that are actually present
            // and mergeable, not the raw id set (which can hold stale ids).
            mergeableCount={selectedThreads.length}
            onToggleSelect={(id: string) =>
              setSelectedIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onMerge={() => {
              if (selectedThreads.length < 2) return;
              setPendingTarget(null);
              setPendingSources([]);
              setView({ kind: 'merge', sources: selectedThreads });
            }}
            onSubmit={(threadId: string) => handleSubmit(threadId, reloadList)}
            onSplit={openSplitFromList}
            onOpen={(threadId: string) => setView({ kind: 'detail', threadId })}
            onDelete={setDeleteTarget}
            onRefresh={reloadList}
            canWrite={canWrite}
            canApprove={canApprove}
            total={store.total}
            page={page}
            pageSize={pageSize}
            onPageChange={p => setPage(p)}
            onPageSizeChange={s => {
              setPageSize(s);
              setPage(0);
            }}
          />
        </Box>
      )}

      {view.kind === 'import' && (
        <RegisterForm onRegister={handleRegister} onBack={goList} />
      )}

      {view.kind === 'detail' && store.thread && (
        <ThreadDetail
          thread={store.thread}
          instances={store.instances}
          lineage={lineage}
          canWrite={canWrite}
          canApprove={canApprove}
          busy={busy || store.detailLoading}
          onBack={() => setView({ kind: 'list' })}
          onEdit={text =>
            withBusy(() => store.editInstance(detailThreadId, text))
          }
          onUpdateMetadata={patch =>
            withBusy(() => store.updateMetadata(detailThreadId, patch))
          }
          onAccept={versionId =>
            withBusy(() => store.acceptInstance(detailThreadId, versionId))
          }
          onReject={versionId =>
            withBusy(() => store.rejectInstance(detailThreadId, versionId))
          }
          onSubmit={() =>
            handleSubmit(detailThreadId, () => {
              void loadDetail(detailThreadId);
            })
          }
          onApprove={async () => {
            setApproveForce(false);
            setApproveConfirmOpen(true);
            return true;
          }}
          onForceApprove={async () => {
            setApproveForce(true);
            setApproveConfirmOpen(true);
            return true;
          }}
          onUnapprove={() =>
            withBusy(() => store.unapproveThread(detailThreadId))
          }
          onDelete={() => setDeleteTarget(store.thread)}
          onSplit={() => {
            setPendingChildren([]);
            if (store.thread) {
              setView({
                kind: 'split',
                source: store.thread,
                sourceText: currentText(store.instances),
              });
            }
          }}
          onReviewSplit={() => {
            // A PENDING_SPLIT child points at its parent, which owns the
            // accept/reject (the parent stays WIP during the proposal).
            const parentId = store.thread?.parentThreadId;
            if (parentId) void openSplitReview(parentId);
          }}
          onReviewMerge={() => {
            // The PENDING_MERGE target owns the accept/reject; a locked source
            // (still WIP) points at it.
            const targetId = mergeReviewTargetId(store.thread);
            if (targetId) void openMergeReview(targetId);
          }}
          onOpenThread={threadId => setView({ kind: 'detail', threadId })}
        />
      )}

      {view.kind === 'split' && (
        <SplitPanel
          source={view.source}
          sourceText={view.sourceText}
          pendingChildren={pendingChildren}
          childrenTexts={pendingChildrenTexts}
          busy={busy}
          reviewMode={view.reviewMode}
          canWrite={canWrite}
          onBack={goList}
          onPropose={handleProposeSplit}
          onAccept={handleAcceptSplit}
          onReject={handleRejectSplit}
        />
      )}

      {view.kind === 'merge' && (
        <MergePanel
          sources={view.sources}
          pendingTarget={pendingTarget}
          pendingSources={pendingSources}
          targetText={pendingTargetText}
          sourcesTexts={pendingSourcesTexts}
          busy={busy}
          reviewMode={view.reviewMode}
          canWrite={canWrite}
          onBack={goList}
          onPropose={handleProposeMerge}
          onAccept={handleAcceptMerge}
          onReject={handleRejectMerge}
          onEditTarget={handleEditMergeTarget}
        />
      )}

      <DeleteThreadDialog
        target={deleteTarget}
        onConfirm={store.deleteThread}
        onDeleted={thread => {
          setDeleteTarget(null);
          setToast(
            t('delete.success', { id: thread.name || `#${thread.threadId}` }),
          );
          // Drop the deleted thread from the Merge selection so it can't linger.
          setSelectedIds(prev => {
            if (!prev.has(thread.threadId)) return prev;
            const next = new Set(prev);
            next.delete(thread.threadId);
            return next;
          });
          // Return to the list and reload it. setView is a no-op when already
          // on the list (delete from a row), so reload explicitly rather than
          // relying on the view-change effect to re-fire.
          setView({ kind: 'list' });
          void reloadList();
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Approve / Force-Approve confirmation dialog (CB25SES-760) */}
      <Dialog
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
      >
        <DialogTitle>
          {approveForce
            ? t('detail.approveConfirm.forceTitle')
            : t('detail.approveConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('detail.approveConfirm.body', {
              id: store.thread?.threadId ?? '',
              name: store.thread?.name ?? '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveConfirmOpen(false)}>
            {t('detail.actions.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={busy}
            onClick={async () => {
              setApproveConfirmOpen(false);
              if (!approveForce) {
                await withBusy(() => store.approveThread(detailThreadId));
                return;
              }
              await withBusy(async () => {
                const result = await store.forceApproveThread(detailThreadId);
                // The gate answers 200 with confirmed=false and changes
                // nothing; show what it objected to instead of leaving the
                // button looking inert.
                if (result && !result.confirmed) {
                  setGateWarnings(result.warnings);
                } else if (result?.confirmed) {
                  // The same outcome as approving past the warnings, so it owes
                  // the user the same acknowledgement. Without this the common
                  // case — no warnings at all — just closed the dialog and
                  // looked like nothing had happened.
                  setToast(t('detail.toasts.forceApproved'));
                }
                return true;
              });
            }}
          >
            {approveForce
              ? t('detail.actions.forceApprove')
              : t('detail.actions.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <ApprovalWarningsDialog
        open={gateWarnings !== null}
        warnings={gateWarnings ?? []}
        busy={busy}
        onCancel={() => setGateWarnings(null)}
        onApproveAnyway={async () => {
          setGateWarnings(null);
          await withBusy(async () => {
            const result = await store.forceApproveThread(detailThreadId, true);
            if (result?.confirmed) {
              setToast(t('detail.toasts.forceApproved'));
            }
            return true;
          });
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </>
  );
};

/**
 * SC-U15. The screen keeps its own top-level route; the project it works on
 * comes from the active-project context, not from the
 * path.
 */
export const RequirementLifecyclePage: FC = () => {
  const { t } = useTranslation('requirement-lifecycle');

  // Set the topbar title/subtitle for this page.
  useSetTopBarTitle(t('title'), t('subtitle'));

  return (
    <Page themeId="home">
      <Content>
        <RequireActiveProject>
          {projectId => (
            // Keyed by project so switching one discards every piece of
            // per-project state at once — the thread list, the current view,
            // and above all `selectedIds`, which would otherwise carry thread
            // ids from the previous project into the next analysis call.
            <RequirementLifecycleView key={projectId} projectId={projectId} />
          )}
        </RequireActiveProject>
      </Content>
    </Page>
  );
};

export default RequirementLifecyclePage;
